import { z } from "zod/v4";

import { ShopifyCountryCodeSchema } from "../schemas/shopify";
import { ConfigurationResponseDtoSchema } from "./configurations";
import { BaseIntegrationDtoSchema } from "./integrations";
import { SubscriptionStatusList } from "./organization-subscriptions";
import { ProcessStatusTableDtoSchema } from "./processes";
import { ActiveStripeSubscriptionDtoSchema } from "./stripe-subscription-links";
import { UserDtoSchema } from "./users";
import { BillingCommandBillingRegimeList } from "./billing/commands/catalog";

export const IntegrationStatusList = [
  "active",
  "stale",
  "not_integrated",
  "completed_typeform",
  "collab_access_verified",
  "integration_scheduled",
  "integration_complete",
] as const;

export type IntegrationStatus = (typeof IntegrationStatusList)[number];

export const IntegrationCompleteStatuses: IntegrationStatus[] = [
  "active",
  "completed_typeform",
  "integration_complete",
];

export const OrgAuthDtoSchema = z.object({
  accessToken: z.string(),
  shopId: z.string(),
});

export type OrgAuthDto = z.infer<typeof OrgAuthDtoSchema>;

export const InstallSourceDtoSchema = z.object({
  utmSource: z.string().nullish(),
  utmMedium: z.string().nullish(),
  utmCampaign: z.string().nullish(),
  utmTerm: z.string().nullish(),
  utmContent: z.string().nullish(),
  installedAtTs: z.number().nullish(),
});

export type InstallSourceDto = z.infer<typeof InstallSourceDtoSchema>;

export const ShopCountryCodeSchema = ShopifyCountryCodeSchema;

export const OrgShopCurrencyDtoSchema = z.object({
  locale: z.string().default("en"),
  code: z.string().default("USD"),
  countryCode: ShopCountryCodeSchema.optional(),
});

export type OrgShopCurrencyDto = z.infer<typeof OrgShopCurrencyDtoSchema>;

export const FullOrganizationDtoSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  apiKey: z.string().nullish(),
  lastUpdateTs: z.number(),
  createdAtTs: z.number(),
  accessToken: z.string().nullish(),
  storefrontToken: z.string().nullish(),
  writeStorefrontToken: z.string().nullish(),
  shopId: z.string().nullish(),
  integrationStatus: z.enum(IntegrationStatusList).default("not_integrated"),
  customerEmail: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  scopes: z.string().array().nullish().default([]),
  domain: z.string().nullish(),
  secondaryDomains: z.string().array().nullish().default([]),
  integrations: BaseIntegrationDtoSchema.array().default([]),
  subscriptionId: z.uuid().nullish(),
  subscriptionStatus: z.enum(SubscriptionStatusList),
  activeSubscriptions: ActiveStripeSubscriptionDtoSchema.array().default([]),
  alerts: ProcessStatusTableDtoSchema.array().default([]),
  freeTrialExpiresAtTs: z.number().nullish(),
  configurations: ConfigurationResponseDtoSchema.array(),
  orderCount: z
    .number()
    .nullish()
    .describe(
      "Estimated monthly order count used to determine the organization's Intelligems tier"
    ),
  billingRegime: z.enum(BillingCommandBillingRegimeList).nullish(),
  timezone: z.string().nullish(),
  installCount: z.number(),
  installed: z.boolean(),
  hasStorefrontToken: z.boolean(),
  installSource: InstallSourceDtoSchema.nullish(),
});

// `writeStorefrontToken` is omitted alongside the admin credentials: it carries
// the org's full storefront grant, `unauthenticated_write_*` included, and is
// server-only. `storefrontToken` stays — it is delegated only what the plugin
// uses and is already public in the plugin config.
export const OrganizationDtoSchema = FullOrganizationDtoSchema.omit({
  apiKey: true,
  accessToken: true,
  writeStorefrontToken: true,
});

export type OrganizationDto = z.infer<typeof OrganizationDtoSchema>;

export const CreateOrganizationDtoSchema = OrganizationDtoSchema.omit({
  lastUpdateTs: true,
  createdAtTs: true,
});

export const OrganizationWithAuthDtoSchema = OrganizationDtoSchema.extend({
  shopId: z.string(),
  accessToken: z.string(),
  storefrontToken: z.string().nullish(),
  writeStorefrontToken: z.string().nullish(),
});

export type OrganizationWithAuthDto = z.infer<
  typeof OrganizationWithAuthDtoSchema
>;

export const OrganizationWithPossibleAuthDtoSchema =
  OrganizationDtoSchema.extend({
    accessToken: z.string().optional(),
  });

export type OrganizationWithPossibleAuthDto = z.infer<
  typeof OrganizationWithPossibleAuthDtoSchema
>;

export const InternalOrganizationDtoSchema = OrganizationDtoSchema.extend({
  users: UserDtoSchema.array(),
  numCreatedExperiences: z.number().nullish(),
  numStartedExperiences: z.number().nullish(),
  currencyCode: z
    .string()
    .nullable()
    .describe(
      "ISO currency code from the org's shop_currency configuration. Null when the org has no such configuration."
    ),
}).omit({
  configurations: true,
});

export type InternalOrganizationDto = z.infer<
  typeof InternalOrganizationDtoSchema
>;

export type OrganizationShopIdName = { shopId: string; name: string };

export type IdName = { id: string; name: string };

export type ShopGlobalMetafield = Record<string, never>;

export const RedactedOrganizationSchema = OrganizationDtoSchema.omit({
  integrations: true,
  activeSubscriptions: true,
  configurations: true,
  scopes: true,
  alerts: true,
});

export type RedactedOrganizationDto = z.infer<
  typeof RedactedOrganizationSchema
>;
